import React, { useEffect, useState } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useMediaQuery } from 'react-responsive';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import PhoneInput from 'react-phone-input-2';
import TagManager from 'react-gtm-module';

import GoogleLogin from 'features/session/LoginGoogle';

import Text from 'common/components/Text/Text';
import BannerCarousel from 'features/session/BannerCarousel';
import { useAddPostulationMutation, useAddSessionMutation } from 'services/exploreApi';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { loginSuccess, SessionState } from 'features/session/sessionSlice';
import { CommonHash, User } from 'app/types';

import styles from './Login.module.scss';
import { Logo } from 'assets/landing/index';
import { ReactComponent as CloseIcon } from 'assets/profile/close.svg';
import { setApplicationStarted, setLastStepDone } from 'features/user/userSlice';

const SignupSchema = yup.object().shape({
  email: yup.string().email('Email is not valid').required('Email is required').label('Email'),
  password: yup
    .string()
    .min(8)
    .max(32)
    .matches(/[0-9]/)
    .matches(/[a-z]/)
    .matches(/[A-Z]/)
    .matches(/\d/)
    .required('Password is required')
    .label('Password'),
});

const UserSchema = yup.object().shape({
  name: yup.string().min(3).max(32).required().label('Name'),
  phone: yup.string().min(8),
});

interface SignupInfo {
  email: string;
  password: string;
}

interface UserInfo {
  name: string;
  phone: string;
}

const Register = (): React.ReactElement => {
  const { google_token } = useAppSelector(({ session }: { session: SessionState }) => session);
  const [addSession, { isLoading }] = useAddSessionMutation();
  const [submitPostulation, { isLoading: postulationLoading }] = useAddPostulationMutation();
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [isFirstQuestion, setIsFirstQuestion] = useState<boolean>(true);
  const [isPhoneValid, setIsPhoneValid] = useState<boolean>(true);
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [acceptTerms, setAcceptTerms] = useState<boolean>(false);
  const [googleSuccess, setGoogleSuccess] = useState<boolean>(false);
  const [warningAdvice, setWarningAdvice] = useState<string>('');
  const [fromLogin, setFromLogin] = useState<boolean>(false);
  const passwordValid = useState<CommonHash>({ case: false, number: false, length: false });
  const [userData, setUserData] = useState<CommonHash>({});
  const [personalStatementFlow, setPersonalStatementFlow] = useState<boolean>(
    location.pathname.includes('flow=personal-statement-builder')
  );
  const searchUrlParams = new URLSearchParams(location.search);
  const utmSource = searchUrlParams.get('utm_source') || undefined;
  const utmMedium = searchUrlParams.get('utm_medium') || undefined;
  const utmContent = searchUrlParams.get('utm_content') || undefined;
  const utmCampaign = searchUrlParams.get('utm_campaign') || undefined;
  const utmTerm = searchUrlParams.get('utm_term') || undefined;
  const utmKeywords = searchUrlParams.get('utm_keywords') || undefined;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const handleApply = (programId: number) => {
    const values = { program_id: programId };
    // if (logged) {
    submitPostulation(values).then((resp: any) => {
      dispatch(setApplicationStarted(true));
      dispatch(setLastStepDone(6));
      navigate(`/home/applications/${resp.data.data.id}`);
    });
    // } else {
    //   setTimeout(() => {
    //     console.log('no estai logeado');
    //     // navigate(`/signup?apply_pid=${program?.data.id}&apply_program_name=${program?.data.name}`);
    //   }, 100);
    // }
  };
  const programIdToApply = searchParams.get('apply_pid');
  // const programNameToApply = searchParams.get('apply_program_name');

  const handleNavigateLogin = () => {
    if (programIdToApply) {
      navigate(`/login?apply_pid=${programIdToApply}`);
    } else {
      navigate('/login');
    }
  };

  const prevQuestion = () => {
    isFirstQuestion ? navigate(-1) : setIsFirstQuestion(true);
  };

  const sendGTMEvents = () => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'ga_event',
        eventName: googleSuccess ? 'user_signup_google' : 'user_signup_normal',
      },
    });
  };

  const formikSignup = useFormik<SignupInfo>({
    initialValues: { email: '', password: '' },
    validationSchema: SignupSchema,
    onSubmit: (signupInfo: SignupInfo) => {
      setIsFirstQuestion(false);
      setUserData((u: any) => ({ ...u, ...signupInfo }));
    },
  });

  const formikUser = useFormik<UserInfo>({
    initialValues: { name: '', phone: '' },
    validationSchema: UserSchema,
    onSubmit: (userInfo: UserInfo) => {
      const validPhone = validatePhone();
      if (validPhone) {
        const signupData = {
          ...userData,
          name: userInfo.name,
          phone: formatPhoneNumberIntl('+' + phoneNumber),
          utm_medium: utmMedium,
          utm_source: utmSource,
          utm_term: utmTerm,
          utm_campaign: utmCampaign,
          utm_content: utmContent,
          utm_keywords: utmKeywords,
        };
        addSession({ user: signupData })
          .unwrap()
          .then((resp: CommonHash) => {
            setWarningAdvice('');
            const user = {
              ...resp.response,
              accessToken: resp.auth,
            } as User;
            dispatch(loginSuccess({ user }));
            sendGTMEvents();
            if (location.pathname.includes('flow=profiling')) {
              setTimeout(() => {
                navigate('/profile');
              }, 50);
            } else if (programIdToApply) {
              setTimeout(() => {
                // navigate(detailUrl('programs', parseInt(programIdToApply, 10), programNameToApply));
                handleApply(parseInt(programIdToApply, 10));
              }, 50);
            } else if (personalStatementFlow) {
              setTimeout(() => {
                // navigate(detailUrl('programs', parseInt(programIdToApply, 10), programNameToApply));
                navigate('/personal-statement-builder');
              }, 50);
            } else {
              navigate('/home');
            }
          })
          .catch((err: any): void => {
            if (err && err.data?.meta?.errors?.email) {
              setWarningAdvice('Email already registered, enter from the login');
            } else {
              setWarningAdvice('An error occurred, please try again');
            }
          });
      }
    },
  });

  const handlePhoneChange = (e: any) => {
    setPhoneNumber(e);
  };

  const validatePhone = () => {
    if (phoneNumber && phoneNumber.length >= 8) {
      setIsPhoneValid(true);
      return true;
    } else {
      setIsPhoneValid(false);
      return false;
    }
  };

  useEffect(() => {
    document.title = 'Unify | Signup';
    if (searchParams.get('google_login')) {
      setUserData({ provider: 'google_oauth2', token: google_token });
      setIsFirstQuestion(false);
      setGoogleSuccess(true);
      setFromLogin(true);
    }
  }, []);

  useEffect(() => {
    const pswd = formikSignup.values.password;
    passwordValid[0].case = /[A-Z]/.test(pswd) && /[a-z]/.test(pswd) ? true : false;
    passwordValid[0].number = /\d/.test(pswd) ? true : false;
    passwordValid[0].length = pswd.length >= 8 ? true : false;
  }, [passwordValid, formikSignup.values.password]);

  useEffect(() => {
    if (googleSuccess) {
      setIsFirstQuestion(false);
    }
  }, [googleSuccess]);

  return (
    <div className={isMobile ? '' : styles.desktopContainer}>
      {!isMobile && (
        <div className={styles.closeModal}>
          <CloseIcon className={styles.closeIcon} onClick={() => navigate(-1)} />
        </div>
      )}
      <div className={isMobile ? '' : styles.desktopBox}>
        <div className={`${styles.signupContainer}`}>
          {(isMobile || !isFirstQuestion) && (
            <div
              className={styles.prevPageIcon}
              onClick={prevQuestion}
              style={{ display: googleSuccess ? 'none' : '' }}
            >
              <i className="bi bi-chevron-left"></i>
            </div>
          )}
          {isMobile && <img src={Logo} alt="logo" className={styles.logoContainer} />}
          <Text size={24} weight={700}>
            {fromLogin ? 'Welcome' : 'Sign up'}
          </Text>
          <GoogleLogin
            googleSuccess={googleSuccess}
            setGoogleSuccess={setGoogleSuccess}
            setUserData={setUserData}
          />
          <div className={styles.divider}>
            <ul />
            <Text size={10} color="secondary">
              OR
            </Text>
            <ul />
          </div>
          <form
            onSubmit={isFirstQuestion ? formikSignup.handleSubmit : formikUser.handleSubmit}
            id="signupForm"
          >
            {isFirstQuestion ? (
              <>
                <div className={styles.inputGroup}>
                  <Text
                    size={14}
                    weight={500}
                    inlineStyle={{ marginBottom: '5px' }}
                    color="primary"
                  >
                    Email
                  </Text>
                  <input
                    className={`form-control ${
                      formikSignup.errors.email && formikSignup.touched.email && styles.warningInput
                    }`}
                    key="email"
                    name="email"
                    type="text"
                    placeholder="Enter your email"
                    value={formikSignup.values.email}
                    onChange={formikSignup.handleChange}
                  />
                  {formikSignup.errors.email && formikSignup.touched.email && (
                    <p className={styles.warningLabel}>{formikSignup.errors.email}</p>
                  )}
                </div>
                <div className={styles.inputGroup}>
                  <Text
                    size={14}
                    weight={500}
                    inlineStyle={{ marginBottom: '5px' }}
                    color="primary"
                  >
                    Password
                  </Text>
                  <input
                    className={`form-control ${
                      formikSignup.errors.password &&
                      formikSignup.touched.password &&
                      styles.warningInput
                    }`}
                    name="password"
                    value={formikSignup.values.password}
                    onChange={formikSignup.handleChange}
                    type="password"
                    placeholder="******"
                  />
                  {formikSignup.errors.password && formikSignup.touched.password && (
                    <p className={styles.warningLabel}>Password is not vlaid</p>
                  )}
                  <div className={styles.pswdRequirementsContainer}>
                    <div className={styles.pswdRequirement}>
                      <i
                        className={`bi bi-check-circle${passwordValid[0].case ? '-fill' : ''}`}
                        style={{ color: passwordValid[0].case ? '#A1E4A1' : '#B6C2E0' }}
                      ></i>
                      <Text size={12} color="secondary">
                        includes lower and upper case
                      </Text>
                    </div>
                    <div className={styles.pswdRequirement}>
                      <i
                        className={`bi bi-check-circle${passwordValid[0].number ? '-fill' : ''}`}
                        style={{ color: passwordValid[0].number ? '#A1E4A1' : '#B6C2E0' }}
                      ></i>
                      <Text size={12} color="secondary">
                        includes at lease one number
                      </Text>
                    </div>
                    <div className={styles.pswdRequirement}>
                      <i
                        className={`bi bi-check-circle${passwordValid[0].length ? '-fill' : ''}`}
                        style={{ color: passwordValid[0].length ? '#A1E4A1' : '#B6C2E0' }}
                      ></i>
                      <Text size={12} color="secondary">
                        password must be 8 or more characters
                      </Text>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className={styles.inputGroup}>
                  <Text
                    size={14}
                    weight={500}
                    inlineStyle={{ marginBottom: '5px' }}
                    color="primary"
                  >
                    Name
                  </Text>
                  <input
                    className={`form-control ${
                      formikUser.errors.name && formikUser.touched.name && styles.warningInput
                    }`}
                    key="name"
                    name="name"
                    type="text"
                    placeholder="Enter your name"
                    value={formikUser.values.name}
                    onChange={formikUser.handleChange}
                  />
                  {formikUser.errors.name && formikUser.touched.name && (
                    <p className={styles.warningLabel}>{formikUser.errors.name}</p>
                  )}
                </div>
                <div className={styles.inputGroup}>
                  <Text
                    size={14}
                    weight={500}
                    inlineStyle={{ marginBottom: '5px' }}
                    color="primary"
                  >
                    Phone number
                  </Text>
                  <PhoneInput
                    inputClass={`form-control ${styles.phoneInput} ${
                      !isPhoneValid && styles.warningInput
                    }`}
                    country={'gb'}
                    countryCodeEditable={false}
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                  />
                  {!isPhoneValid && (
                    <label className={styles.warningLabel}>Phone number is not valid</label>
                  )}
                </div>
                <div className={`form-check mt-2`}>
                  <input
                    id="agree_with_terms"
                    className="form-check-input"
                    type="checkbox"
                    name="flexRadioDefault"
                    checked={acceptTerms}
                    onChange={() => setAcceptTerms(!acceptTerms)}
                  />
                  <label
                    className={`form-check-label ${styles.formLabel}`}
                    htmlFor="agree_with_terms"
                  >
                    I agree with Unify Terms and conditions
                  </label>
                </div>
              </>
            )}
            {warningAdvice && !isFirstQuestion && (
              <label className={styles.warningLabel}>{warningAdvice}</label>
            )}
            <button
              className={`${styles.nextButton} ${
                !isFirstQuestion && (acceptTerms ? '' : styles.submitBtnDisabled)
              }`}
              type="submit"
              disabled={(!isFirstQuestion && !acceptTerms) || isLoading ? true : false}
            >
              {isLoading || postulationLoading ? (
                <div className={`spinner-border ${styles.loading}`} role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              ) : isFirstQuestion ? (
                'Next'
              ) : (
                'Sign up'
              )}
            </button>
          </form>
          <div className={styles.bottomText}>
            <Text size={14} color="secondary">
              Have an account?{' '}
            </Text>
            <div className={styles.loginLink} onClick={handleNavigateLogin}>
              Log in
            </div>
          </div>
        </div>
        {!isMobile && <BannerCarousel />}
      </div>
    </div>
  );
};

export default Register;
